import { Card, SxProps, Theme } from '@mui/material';
import { ListTable, ListTableProps, ListTableRow } from 'framework/ui/ui-builder';
import type { ColumnVisibilityControl } from 'framework/ui/ui-builder/ListTable/types';
import type { CollectionColumnsOptions } from './useCollectionColumns/types';
import LoadError from '../../components/LoadError';
import { mergeSx } from 'framework/utils';

type CollectionListProps<Props> = Props extends unknown ? Omit<Props, 'retryLoadMore'> : never;

export interface CollectionViewProps<Row extends ListTableRow = ListTableRow> {
  listProps: CollectionListProps<ListTableProps<Row>>;
  error: Error | null;
  retry: () => void | Promise<unknown>;
  loading: boolean;
  columnVisibilityControl?: ColumnVisibilityControl;
  columnVisibility?: CollectionColumnsOptions;
  highlightQuery?: string;
  sx?: SxProps<Theme>;
}

const cardSx: SxProps<Theme> = {
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
  p: 1,
  overflow: 'hidden',
  alignSelf: 'stretch',
  width: '100%',
  maxWidth: '100%',
};

const CollectionView = <Row extends ListTableRow>({
  sx,
  listProps,
  columnVisibilityControl,
  error,
  retry,
  loading,
}: CollectionViewProps<Row>) => {
  const { loadMoreError, ...tableProps } = listProps;
  const resolvedTableProps: ListTableProps<Row> = tableProps.rows
    ? { ...tableProps, loadMoreError, retryLoadMore: retry }
    : tableProps;
  const hasRows = Boolean(listProps.rows?.length || listProps.groups?.some(group => group.rows.length));
  const showError = Boolean(error && !listProps.loadMoreError);
  return (
    <Card sx={mergeSx(cardSx, showError && { flexDirection: 'column' }, sx)}>
      {showError && (
        <LoadError
          message={hasRows ? 'Could not refresh records. Showing previously loaded data.' : 'Could not load records.'}
          retry={retry}
          loading={loading}
        />
      )}
      {((!error && !loading) || hasRows) && (
        <ListTable
          {...resolvedTableProps}
          emptyContent={loading ? '' : listProps.emptyContent}
          columnVisibilityControl={columnVisibilityControl}
        />
      )}
    </Card>
  );
};

export default CollectionView;
